/**
 * テロップの回転 (傾き) の正本。
 * docs/features/video-editor-capability-gaps-plan.md §4.2「テロップ回転」。
 *
 * 同じ意味・同じ規則の実装が 3 か所にある (どれかを変えたら必ず 3 つとも変える):
 * - ここ (image-generator FE: プレビュー・UI・書き出し)
 * - video-editor リポジトリの src/schema/captionRotation.ts (Cloud Run の MP4 レンダラー)
 * - backend/services/video_edit/caption_extensions.py (AI 提案の正規化・合成フレーム)
 *
 * ## 形
 *
 * `CaptionDesign.rotationDeg` (数値・-30〜30・0.5 刻み)。**正の値は時計回り**。
 * 丸めた結果が 0 ならキーごと落とす (保存値 = 実効値)。
 *
 * **帯 (band) と画面端の配置 (header / footer) では効かない** (正規化でキーごと落とす)。
 * 帯は画面幅いっぱいに敷く層なので、回すと片側に隙間ができて反対側がはみ出す。header / footer は
 * 画面端に密着させる配置なので、傾けると必ず端から浮く / はみ出す。横位置の補正 (offsetXPercent)
 * と同じ扱い。
 *
 * ## 描画
 *
 * テロップ枠 (背景 / 縁取り / 影 / 文字をまとめたブロック) 全体に `rotate()` を掛ける。
 * 回転の中心は**ブロックの中心** (横は揃え (align) の基準点 + 横補正、縦はブロックの上下中央)。
 * 入場 / 退場アニメーション (§4.2-B1) とは**別の層**で合成し、順序は「アニメ (外) → 回転 (内)」。
 * こうするとアニメの移動方向 (slide / pop) は画面基準のまま、文字だけが傾く。
 * 部分強調・単語ハイライト・タイプライターはブロックの内側なので回転の影響を受けない。
 *
 * フィット縮小とはみ出しクランプは**回転後の外接矩形** (`rotatedBoundingBox`) で判定する:
 * - フィット: 回転後の横幅がセーフエリアに収まるまで文字サイズを縮める
 * - クランプ: 回転で上下に伸びるぶん (`rotatedCaptionClampMarginPx`) を余白として足してから、
 *   従来の `clampCaptionBlockTop` に通す
 *
 * 描画の実装は 4 か所 (どれかを直したら 4 つとも直す):
 * 1. プレビュー `remotion/EditVideo.tsx` の DesignedCaption (横書き)
 * 2. プレビュー `remotion/VerticalCaption.tsx` (縦書き)
 * 3. MP4 レンダラー (video-editor リポジトリの同名ファイル)
 * 4. 批評・ディレクターが見る合成フレーム `backend/services/video_edit/contact_sheet.py`
 *    (文字層を PIL の rotate(expand=True) で回してブロック中心を合わせて合成する)
 *
 * ## 書き出し
 *
 * Premiere (FCP7 XML) は Basic Motion の Rotation + Anchor Point、Final Cut は adjust-transform の
 * rotation で**そのまま出せる** (§0-2 の「落として注記」には該当しない)。ただし回転の中心は
 * テロップブロックの中心の近似で、左右揃え (align left / right) では文字の中心とわずかにずれる。
 */

/** 回転角の範囲 (度)。正 = 時計回り。 */
export const CAPTION_ROTATION_MIN_DEG = -30;
export const CAPTION_ROTATION_MAX_DEG = 30;
/** 保存・UI の刻み (度)。 */
export const CAPTION_ROTATION_STEP_DEG = 0.5;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** half-up の丸め (captionEmphasis / captionWordHighlight の roundToStep と同じ式)。 */
function roundToStep(value: number, step: number): number {
  const inv = 1 / step;
  return Math.floor(value * inv + 0.5) / inv;
}

/**
 * 回転角 (度) を保存形へ正規化する。
 * ±30 にクランプ → 0.5 刻みに丸め → **丸めた結果が 0 ならキーごと落とす**
 * (0.2° を「回転あり」として保存しないため。位置補正 normalizeCaptionOffsetPercentValue と同じ作法)。
 *
 * 帯 (band) / header / footer で落とす判定は design 全体を見ないと決まらないので、ここではなく
 * schema/project.ts の resolveCaptionRotationDeg が持つ。
 */
export function normalizeCaptionRotationValue(value: unknown): number | undefined {
  if (!isFiniteNumber(value)) return undefined;
  const rounded = roundToStep(
    clamp(value, CAPTION_ROTATION_MIN_DEG, CAPTION_ROTATION_MAX_DEG),
    CAPTION_ROTATION_STEP_DEG,
  );
  return rounded === 0 ? undefined : rounded;
}

/**
 * 幅 `w` × 高さ `h` の矩形を `deg` 度回したときの**外接矩形**のサイズ。
 *
 *   width  = w·|cos| + h·|sin|
 *   height = w·|sin| + h·|cos|
 *
 * フィット縮小 (回転後の横幅をセーフエリアに収める) と、はみ出しクランプ (回転で上下に伸びるぶんを
 * 余白として見込む) の両方がこれを使う。`deg` が 0 なら `{w, h}` をそのまま返す (回転の無い
 * テロップの見た目を 1px も変えないため)。
 */
export function rotatedBoundingBox(w: number, h: number, deg: number): { width: number; height: number } {
  const width = isFiniteNumber(w) ? Math.max(0, w) : 0;
  const height = isFiniteNumber(h) ? Math.max(0, h) : 0;
  if (!isFiniteNumber(deg) || deg === 0) return { width, height };
  const rad = (deg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  return {
    width: width * cos + height * sin,
    height: width * sin + height * cos,
  };
}

/**
 * 回転したブロックが上下にはみ出さないよう、クランプに足す片側の余白 (px)。
 *
 * ブロックの中心を軸に回すので、回転で増えた高さの**半分**が上下それぞれに必要になる。
 * 呼び出し側は `clampCaptionBlockTop(top - margin, blockHeight + margin * 2, outputHeight) + margin`
 * の形で使う (margin = 0 なら従来の式と完全に同じ)。
 */
export function rotatedCaptionClampMarginPx(blockWidthPx: number, blockHeightPx: number, deg: number): number {
  if (!isFiniteNumber(deg) || deg === 0) return 0;
  const { height } = rotatedBoundingBox(blockWidthPx, blockHeightPx, deg);
  const base = isFiniteNumber(blockHeightPx) ? Math.max(0, blockHeightPx) : 0;
  return Math.max(0, (height - base) / 2);
}

/** 要約・UI の表示名 (「+8°」「-12.5°」)。0 は空文字列。 */
export function captionRotationLabel(deg: number | undefined | null): string {
  const value = normalizeCaptionRotationValue(deg);
  if (value === undefined) return '';
  return `${value > 0 ? '+' : ''}${value}°`;
}
